import React, { useState, useEffect } from 'react'
import { Calendar, Clock, Video, MessageCircle, CheckCircle, XCircle, AlertCircle, User } from 'lucide-react'
import { Session, GuruConnectApi } from '../../services/guruConnectApi'

interface SessionsManagerProps {
  userRole: 'guru' | 'client'
}

export const SessionsManager: React.FC<SessionsManagerProps> = ({ userRole }) => {
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedSession, setSelectedSession] = useState<Session | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')

  useEffect(() => {
    loadSessions()
  }, [userRole])

  const loadSessions = async () => {
    try {
      setLoading(true)
      const userSessions = await GuruConnectApi.getUserSessions(userRole)
      setSessions(userSessions)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (sessionId: string, newStatus: string) => {
    try {
      await GuruConnectApi.updateSessionStatus(sessionId, newStatus)
      await loadSessions() // Reload sessions
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update session status')
    }
  }

  const filteredSessions = sessions.filter(session => 
    filterStatus === 'all' || session.status === filterStatus
  )

  const getStatusColor = (status: string) => {
    return GuruConnectApi.getSessionStatusColor(status)
  }

  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString)
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    }
  }

  const getOtherParticipant = (session: Session) => {
    if (userRole === 'guru') {
      return session.users ? `${session.users.first_name} ${session.users.last_name}` : 'Unknown Client'
    } else {
      return session.guru_profiles ? GuruConnectApi.getGuruDisplayName(session.guru_profiles) : 'Unknown Guru'
    }
  }

  const getOtherParticipantRole = () => {
    return userRole === 'guru' ? 'Client' : 'Guru'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading sessions...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error Loading Sessions</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={loadSessions}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Sessions</h2>
          <p className="text-gray-600 dark:text-gray-300">
            Manage your {userRole === 'guru' ? 'client' : 'guru'} sessions
          </p>
        </div>
        
        {/* Status Filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        >
          <option value="all">All Sessions</option>
          <option value="scheduled">Scheduled</option>
          <option value="confirmed">Confirmed</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Sessions Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {filterStatus === 'all' 
              ? `You don't have any ${userRole === 'guru' ? 'client' : 'guru'} sessions yet.`
              : `No ${filterStatus} sessions found.`
            }
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredSessions.map((session) => {
            const { date, time } = formatSessionDate(session.scheduled_at)
            const otherParticipant = getOtherParticipant(session)
            
            return (
              <div
                key={session.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {session.title}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                        {GuruConnectApi.getSessionStatusDisplay(session.status)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300">
                        <User className="w-4 h-4" />
                        <span>{getOtherParticipantRole()}: {otherParticipant}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300">
                        <Calendar className="w-4 h-4" />
                        <span>{date}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300">
                        <Clock className="w-4 h-4" />
                        <span>{time} ({session.duration_minutes} min)</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300">
                        <span className="font-medium">
                          {GuruConnectApi.formatPrice((session.guru_profiles?.session_rate || 0) / 60 * session.duration_minutes)}
                        </span>
                      </div>
                    </div>
                    
                    {session.description && (
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {session.description}
                      </p>
                    )}
                    
                    {/* Session Actions */}
                    <div className="flex items-center space-x-3">
                      {session.status === 'scheduled' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(session.id, 'confirmed')}
                            className="flex items-center space-x-1 px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 rounded-md text-sm transition-colors"
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Confirm</span>
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(session.id, 'cancelled')}
                            className="flex items-center space-x-1 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded-md text-sm transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                            <span>Cancel</span>
                          </button>
                        </>
                      )}
                      
                      {session.status === 'confirmed' && (
                        <button
                          onClick={() => handleStatusUpdate(session.id, 'in_progress')}
                          className="flex items-center space-x-1 px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md text-sm transition-colors"
                        >
                          <Video className="w-4 h-4" />
                          <span>Start Session</span>
                        </button>
                      )}
                      
                      {session.status === 'in_progress' && (
                        <button
                          onClick={() => handleStatusUpdate(session.id, 'completed')}
                          className="flex items-center space-x-1 px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 rounded-md text-sm transition-colors"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Complete</span>
                        </button>
                      )}
                      
                      <button
                        onClick={() => setSelectedSession(session)}
                        className="flex items-center space-x-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md text-sm transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>View Details</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Session Details Modal */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Session Details
                </h2>
                <button
                  onClick={() => setSelectedSession(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Session Information</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Title:</span>
                      <span className="text-gray-900 dark:text-white font-medium">{selectedSession.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Type:</span>
                      <span className="text-gray-900 dark:text-white">{selectedSession.session_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedSession.status)}`}>
                        {GuruConnectApi.getSessionStatusDisplay(selectedSession.status)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Duration:</span>
                      <span className="text-gray-900 dark:text-white">{selectedSession.duration_minutes} minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Scheduled:</span>
                      <span className="text-gray-900 dark:text-white">{formatSessionDate(selectedSession.scheduled_at).date} at {formatSessionDate(selectedSession.scheduled_at).time}</span>
                    </div>
                  </div>
                </div>
                
                {selectedSession.description && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Description</h3>
                    <p className="text-gray-600 dark:text-gray-300">{selectedSession.description}</p>
                  </div>
                )}
                
                {selectedSession.notes && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Notes</h3>
                    <p className="text-gray-600 dark:text-gray-300">{selectedSession.notes}</p>
                  </div>
                )}
                
                {selectedSession.meeting_url && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Meeting Link</h3>
                    <a
                      href={selectedSession.meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Join Meeting
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 